// Grupos y agencias (script 935): cupo por tipo y noche, fecha de corte
// (el cierre nocturno libera lo no usado) y lista de huéspedes que crea las
// reservas del grupo a la tarifa de grupo, facturando a la agencia lo que paga.
// Gestión: nivel 4 o más; la lista de huéspedes, nivel 3.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { Campo, Modal, fieldCls, money, hotelRpc, hotelTabla, QK, fmtFecha, sumarDias, hoyISO, useSort, SortTh, ESTADO_RESERVA,
  type TipoHabitacion, type Plan, type EstadoReserva } from './shared';
import { useClientes } from './ClienteHuesped';
import { ReservaDetalle } from './ReservaDetalle';

interface GrupoFila { id: string; codigo: string; nombre: string; agencia: string | null; llegada: string; salida: string; fecha_corte: string; estado: string; cupo: number; pickup: number; reservas: number }
interface Bloque { tipo_id: string; tipo: string; fecha: string; cupo: number; precio: number | null; pickup: number }
interface GrupoDet {
  id: string; codigo: string; nombre: string; partner_id: string | null; contacto: string | null; llegada: string; salida: string; fecha_corte: string;
  plan_id: string | null; plan: string | null; paga_agencia: string[]; estado: string; notas: string | null;
  agencia: { id: string; nombre: string; identificacion: string | null } | null;
  bloques: Bloque[];
  reservas: { id: string; codigo: string; estado: EstadoReserva; huesped: string | null; tipo: string; habitacion: string | null; llegada: string; salida: string; total: number }[];
}
const ESTADOS: Record<string, string> = { tentativo: 'Tentativo', confirmado: 'Confirmado', cerrado: 'Cerrado', cancelado: 'Cancelado' };
const PAGA: Record<string, string> = { hospedaje: 'Alojamiento', servicio: 'Servicio 10 %', tasa: 'Tasa turística', ayb: 'Alimentos del plan', paquete: 'Tours/spa del plan', pos: 'Consumos POS', extra: 'Extras' };
const getVal = (g: GrupoFila, k: string) => (k === 'cupo' || k === 'pickup' || k === 'reservas' ? Number(g[k]) : (g as unknown as Record<string, string | null>)[k]);

export function GruposHotel() {
  const qc = useQueryClient();
  const { hasAccessLevel } = usePermissions();
  const [sel, setSel] = useState<string | null>(null);
  const [nuevo, setNuevo] = useState(false);
  const { data: grupos = [], isLoading } = useQuery<GrupoFila[]>({ queryKey: [QK, 'grupos'], queryFn: () => hotelRpc<GrupoFila[]>('grupos_listar', {}) });
  const sort = useSort(grupos, getVal, 'llegada', 'desc');

  if (sel) return <GrupoDetalle id={sel} onBack={() => setSel(null)} />;

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <h1 className="flex items-center gap-2 text-xl font-semibold"><Users className="h-5 w-5" /> Grupos y agencias</h1>
        {hasAccessLevel(4) && <Button onClick={() => setNuevo(true)}><Plus className="mr-1 h-4 w-4" /> Nuevo grupo</Button>}
      </div>
      <p className="text-sm text-muted-foreground">
        El cupo no usado de un grupo no se vende al público hasta la <strong>fecha de corte</strong>; pasada esa fecha, el cierre nocturno lo libera solo.
      </p>
      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pl-3 pr-3">N°</th>
              <SortTh col="codigo" sort={sort}>Código</SortTh>
              <SortTh col="nombre" sort={sort}>Grupo</SortTh>
              <SortTh col="agencia" sort={sort}>Agencia</SortTh>
              <SortTh col="llegada" sort={sort}>Llegada</SortTh>
              <SortTh col="salida" sort={sort}>Salida</SortTh>
              <SortTh col="fecha_corte" sort={sort}>Corte</SortTh>
              <SortTh col="cupo" sort={sort} align="right">Cupo (noches)</SortTh>
              <SortTh col="pickup" sort={sort} align="right">Tomado</SortTh>
              <SortTh col="reservas" sort={sort} align="right">Reservas</SortTh>
              <SortTh col="estado" sort={sort}>Estado</SortTh>
            </tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={11} className="py-8 text-center text-muted-foreground">Cargando…</td></tr>}
              {!isLoading && grupos.length === 0 && <tr><td colSpan={11} className="py-8 text-center text-muted-foreground">Sin grupos registrados.</td></tr>}
              {sort.sorted.map((g, i) => (
                <tr key={g.id} className="cursor-pointer border-b last:border-0 hover:bg-muted/50" onClick={() => setSel(g.id)}>
                  <td className="py-2 pl-3 pr-3 text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 font-mono">{g.codigo}</td>
                  <td className="py-2 pr-3">{g.nombre}</td>
                  <td className="py-2 pr-3">{g.agencia ?? '—'}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{fmtFecha(g.llegada)}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{fmtFecha(g.salida)}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{fmtFecha(g.fecha_corte)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{g.cupo}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{g.pickup}{g.cupo > 0 && <span className="ml-1 text-xs text-muted-foreground">({Math.round(100 * g.pickup / g.cupo)} %)</span>}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{g.reservas}</td>
                  <td className="py-2 pr-3">{ESTADOS[g.estado] ?? g.estado}</td>
                </tr>
              ))}
            </tbody>
            {grupos.length > 0 && (
              <tfoot><tr className="border-t-2 bg-muted/30 font-semibold">
                <td className="py-2 pl-3" colSpan={7}>Totales ({grupos.length})</td>
                <td className="py-2 pr-3 text-right tabular-nums">{grupos.reduce((a, g) => a + Number(g.cupo), 0)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{grupos.reduce((a, g) => a + Number(g.pickup), 0)}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{grupos.reduce((a, g) => a + Number(g.reservas), 0)}</td>
                <td />
              </tr></tfoot>
            )}
          </table>
        </CardContent>
      </Card>
      {nuevo && <ModalGrupo onClose={() => setNuevo(false)} onDone={(id) => { setNuevo(false); void qc.invalidateQueries({ queryKey: [QK] }); if (id) setSel(id); }} />}
    </div>
  );
}

function ModalGrupo({ grupo, onClose, onDone }: { grupo?: GrupoDet; onClose: () => void; onDone: (id?: string) => void }) {
  const clientes = useClientes();
  const { data: planes = [] } = useQuery<Plan[]>({ queryKey: [QK, 'planes'], queryFn: () => hotelTabla<Plan>('planes', 'es_activo=eq.true&order=orden') });
  const [f, setF] = useState({
    nombre: grupo?.nombre ?? '', partner_id: grupo?.partner_id ?? '', contacto: grupo?.contacto ?? '',
    llegada: grupo?.llegada ?? sumarDias(hoyISO(), 30), salida: grupo?.salida ?? sumarDias(hoyISO(), 33),
    fecha_corte: grupo?.fecha_corte ?? sumarDias(hoyISO(), 15), plan_id: grupo?.plan_id ?? '',
    paga_agencia: grupo?.paga_agencia ?? ['hospedaje', 'servicio', 'tasa', 'ayb', 'paquete'],
    estado: grupo?.estado ?? 'tentativo', notas: grupo?.notas ?? '',
  });
  const guardar = useMutation({
    mutationFn: () => hotelRpc<{ id: string; codigo: string }>('grupo_guardar', { _d: { id: grupo?.id, ...f, partner_id: f.partner_id || null, plan_id: f.plan_id || null,
      contacto: f.contacto.trim() || null, notas: f.notas.trim() || null, ...(grupo ? { llegada: undefined, salida: undefined } : {}) } }),
    onSuccess: (r) => { toast.success(`Grupo ${r.codigo} guardado`); onDone(r.id); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <Modal title={grupo ? `Editar ${grupo.codigo}` : 'Nuevo grupo'} onClose={onClose} wide>
      <div className="space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <Campo label="Nombre del grupo *" className="col-span-2"><input className={fieldCls} value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} /></Campo>
          <Campo label="Estado">
            <select className={fieldCls} value={f.estado} onChange={(e) => setF({ ...f, estado: e.target.value })}>
              {Object.entries(ESTADOS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Campo>
          <Campo label="Agencia / empresa que paga" className="col-span-2">
            <select className={fieldCls} value={f.partner_id} onChange={(e) => setF({ ...f, partner_id: e.target.value })}>
              <option value="">— Cada pasajero paga lo suyo —</option>
              {clientes.map((c) => <option key={c.id} value={c.id}>{c.razon_social} ({c.identificacion ?? 's/i'})</option>)}
            </select>
          </Campo>
          <Campo label="Contacto"><input className={fieldCls} value={f.contacto} onChange={(e) => setF({ ...f, contacto: e.target.value })} /></Campo>
          <Campo label="Llegada"><input type="date" className={fieldCls} disabled={!!grupo} value={f.llegada} onChange={(e) => setF({ ...f, llegada: e.target.value })} /></Campo>
          <Campo label="Salida"><input type="date" className={fieldCls} disabled={!!grupo} value={f.salida} onChange={(e) => setF({ ...f, salida: e.target.value })} /></Campo>
          <Campo label="Fecha de corte"><input type="date" className={fieldCls} value={f.fecha_corte} max={f.llegada} onChange={(e) => setF({ ...f, fecha_corte: e.target.value })} /></Campo>
          <Campo label="Plan del grupo">
            <select className={fieldCls} value={f.plan_id} onChange={(e) => setF({ ...f, plan_id: e.target.value })}>
              <option value="">— Tarifa estándar —</option>
              {planes.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </select>
          </Campo>
        </div>
        {f.partner_id && (
          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground">La agencia paga (lo demás lo paga cada pasajero)</span>
            <div className="grid grid-cols-2 gap-1 md:grid-cols-4">
              {Object.entries(PAGA).map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={f.paga_agencia.includes(k)} onChange={(e) => setF({ ...f, paga_agencia: e.target.checked ? [...f.paga_agencia, k] : f.paga_agencia.filter((x) => x !== k) })} /> {l}
                </label>
              ))}
            </div>
          </div>
        )}
        <Campo label="Notas"><textarea className={`${fieldCls} h-auto`} rows={2} value={f.notas} onChange={(e) => setF({ ...f, notas: e.target.value })} /></Campo>
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={guardar.isPending} disabled={!f.nombre.trim() || !f.llegada || !f.salida || f.salida <= f.llegada} onClick={() => guardar.mutate()}>Guardar</Button>
        </div>
      </div>
    </Modal>
  );
}

interface FilaRooming { tipo_id: string; nombre: string; identificacion: string; adultos: number; ninos: number }

function GrupoDetalle({ id, onBack }: { id: string; onBack: () => void }) {
  const qc = useQueryClient();
  const { hasAccessLevel } = usePermissions();
  const { data: g } = useQuery<GrupoDet>({ queryKey: [QK, 'grupo', id], queryFn: () => hotelRpc<GrupoDet>('grupo_obtener', { _id: id }) });
  const { data: tipos = [] } = useQuery<TipoHabitacion[]>({ queryKey: [QK, 'tipos'], queryFn: () => hotelTabla<TipoHabitacion>('tipos_habitacion', 'es_activo=eq.true&order=orden') });
  const [editar, setEditar] = useState(false);
  const [bl, setBl] = useState({ tipo_id: '', cupo: 1, precio: '' });
  const [lista, setLista] = useState<FilaRooming[]>([]);
  const [detalle, setDetalle] = useState<string | null>(null);
  const refrescar = () => void qc.invalidateQueries({ queryKey: [QK] });

  const setBloque = useMutation({
    mutationFn: () => hotelRpc('grupo_bloque_set', { _grupo: id, _tipo: bl.tipo_id, _desde: g?.llegada, _hasta: g?.salida, _cupo: bl.cupo, _precio: bl.precio === '' ? null : Number(bl.precio) }),
    onSuccess: () => { toast.success('Cupo guardado'); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const rooming = useMutation({
    mutationFn: () => hotelRpc<{ reservas: { codigo: string }[] }>('grupo_rooming', { _grupo: id, _lista: lista.map((x) => ({
      tipo_id: x.tipo_id, adultos: x.adultos, ninos: x.ninos,
      huesped: { nombre: x.nombre.trim(), identificacion: x.identificacion.trim() || null } })) }),
    onSuccess: (r) => { toast.success(`${r.reservas.length} reserva(s) creada(s)`); setLista([]); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Cupo por tipo (resumen de las noches)
  const porTipo = useMemo(() => {
    const m = new Map<string, { tipo: string; cupo: number; pickup: number; precio: number | null; noches: number }>();
    (g?.bloques ?? []).forEach((b) => {
      const x = m.get(b.tipo_id) ?? { tipo: b.tipo, cupo: 0, pickup: 0, precio: b.precio, noches: 0 };
      x.cupo = Math.max(x.cupo, b.cupo); x.pickup = Math.max(x.pickup, b.pickup); x.noches += 1;
      m.set(b.tipo_id, x);
    });
    return [...m.entries()];
  }, [g]);

  if (!g) return <div className="p-6 text-sm text-muted-foreground">Cargando…</div>;
  if (detalle) return <div className="p-6"><ReservaDetalle reservaId={detalle} onClose={() => { setDetalle(null); refrescar(); }} /></div>;
  const abierto = g.estado === 'tentativo' || g.estado === 'confirmado';

  return (
    <div className="space-y-4 p-6 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={onBack}><ArrowLeft className="mr-1 h-4 w-4" /> Grupos</Button>
          <h1 className="text-xl font-semibold">{g.codigo} · {g.nombre}</h1>
          <span className="rounded bg-muted px-2 py-0.5 text-xs">{ESTADOS[g.estado] ?? g.estado}</span>
        </div>
        {hasAccessLevel(4) && <Button variant="outline" size="sm" onClick={() => setEditar(true)}>Editar</Button>}
      </div>
      <p className="text-muted-foreground">
        {fmtFecha(g.llegada)} → {fmtFecha(g.salida)} · corte {fmtFecha(g.fecha_corte)} · {g.agencia ? `paga ${g.agencia.nombre}: ${g.paga_agencia.map((k) => PAGA[k] ?? k).join(', ')}` : 'cada pasajero paga lo suyo'}
        {g.plan ? ` · plan ${g.plan}` : ''}
      </p>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Cupo por tipo de habitación</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <table className="w-full text-xs">
              <thead><tr className="border-b text-left text-muted-foreground"><th className="py-1 pr-2">Tipo</th><th className="py-1 pr-2 text-right">Cupo/noche</th><th className="py-1 pr-2 text-right">Tomado</th><th className="py-1 pr-2 text-right">Tarifa grupo</th></tr></thead>
              <tbody>
                {porTipo.length === 0 && <tr><td colSpan={4} className="py-3 text-center text-muted-foreground">Sin cupo asignado</td></tr>}
                {porTipo.map(([tid, x]) => (
                  <tr key={tid} className="border-b last:border-0">
                    <td className="py-1 pr-2">{x.tipo}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{x.cupo}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{x.pickup}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{x.precio != null ? money(Number(x.precio)) : 'normal'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {abierto && hasAccessLevel(4) && (
              <div className="grid grid-cols-2 items-end gap-2 md:grid-cols-4">
                <Campo label="Tipo" className="col-span-2">
                  <select className={fieldCls} value={bl.tipo_id} onChange={(e) => setBl({ ...bl, tipo_id: e.target.value })}>
                    <option value="">— Selecciona —</option>
                    {tipos.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
                  </select>
                </Campo>
                <Campo label="Habitaciones"><input type="number" min={0} className={fieldCls} value={bl.cupo} onChange={(e) => setBl({ ...bl, cupo: Math.max(0, Number(e.target.value) || 0) })} /></Campo>
                <Campo label="Tarifa grupo (vacío = normal)"><input type="number" min={0} step="0.01" className={fieldCls} value={bl.precio} onChange={(e) => setBl({ ...bl, precio: e.target.value })} /></Campo>
                <Button className="col-span-2 md:col-span-4" size="sm" variant="outline" isLoading={setBloque.isPending} disabled={!bl.tipo_id} onClick={() => setBloque.mutate()}>
                  Guardar cupo para todas las noches del grupo
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Lista de huéspedes (rooming list)</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {!abierto && <p className="text-xs text-muted-foreground">El grupo está {ESTADOS[g.estado]?.toLowerCase()}.</p>}
            {abierto && <>
              {lista.map((x, i) => (
                <div key={i} className="grid grid-cols-2 items-end gap-2 rounded border p-2 md:grid-cols-6">
                  <Campo label="Huésped" className="col-span-2"><input className={fieldCls} value={x.nombre} onChange={(e) => setLista(lista.map((y, j) => (j === i ? { ...y, nombre: e.target.value } : y)))} /></Campo>
                  <Campo label="Identificación"><input className={fieldCls} value={x.identificacion} onChange={(e) => setLista(lista.map((y, j) => (j === i ? { ...y, identificacion: e.target.value } : y)))} /></Campo>
                  <Campo label="Tipo">
                    <select className={fieldCls} value={x.tipo_id} onChange={(e) => setLista(lista.map((y, j) => (j === i ? { ...y, tipo_id: e.target.value } : y)))}>
                      {porTipo.map(([tid, t]) => <option key={tid} value={tid}>{t.tipo}</option>)}
                    </select>
                  </Campo>
                  <Campo label="Adultos"><input type="number" min={1} className={fieldCls} value={x.adultos} onChange={(e) => setLista(lista.map((y, j) => (j === i ? { ...y, adultos: Math.max(1, Number(e.target.value) || 1) } : y)))} /></Campo>
                  <div className="flex items-end gap-1">
                    <Campo label="Niños"><input type="number" min={0} className={fieldCls} value={x.ninos} onChange={(e) => setLista(lista.map((y, j) => (j === i ? { ...y, ninos: Math.max(0, Number(e.target.value) || 0) } : y)))} /></Campo>
                    <button type="button" className="mb-2 rounded p-1 text-red-500 hover:bg-muted" onClick={() => setLista(lista.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
              <div className="flex justify-between gap-2">
                <Button size="sm" variant="outline" disabled={porTipo.length === 0}
                  onClick={() => setLista([...lista, { tipo_id: porTipo[0]?.[0] ?? '', nombre: '', identificacion: '', adultos: 2, ninos: 0 }])}>
                  <Plus className="mr-1 h-3.5 w-3.5" /> Habitación
                </Button>
                <Button size="sm" isLoading={rooming.isPending} disabled={lista.length === 0 || lista.some((x) => !x.nombre.trim() || !x.tipo_id)} onClick={() => rooming.mutate()}>
                  Crear {lista.length} reserva(s)
                </Button>
              </div>
              {porTipo.length === 0 && <p className="text-xs text-muted-foreground">Primero asigne cupo al grupo.</p>}
            </>}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Reservas del grupo ({g.reservas.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">N°</th><th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Huésped</th><th className="py-2 pr-3">Tipo / hab.</th>
              <th className="py-2 pr-3">Llegada</th><th className="py-2 pr-3">Salida</th><th className="py-2 pr-3">Estado</th><th className="py-2 pr-3 text-right">Total</th>
            </tr></thead>
            <tbody>
              {g.reservas.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">Sin reservas aún.</td></tr>}
              {g.reservas.map((r, i) => (
                <tr key={r.id} className="cursor-pointer border-b last:border-0 hover:bg-muted/50" onClick={() => setDetalle(r.id)}>
                  <td className="py-1.5 pr-3 text-muted-foreground">{i + 1}</td>
                  <td className="py-1.5 pr-3 font-mono">{r.codigo}</td>
                  <td className="py-1.5 pr-3">{r.huesped ?? '—'}</td>
                  <td className="py-1.5 pr-3">{r.tipo}{r.habitacion ? ` · ${r.habitacion}` : ''}</td>
                  <td className="py-1.5 pr-3">{fmtFecha(r.llegada)}</td>
                  <td className="py-1.5 pr-3">{fmtFecha(r.salida)}</td>
                  <td className="py-1.5 pr-3"><span className={`rounded px-2 py-0.5 text-xs ${ESTADO_RESERVA[r.estado]?.cls ?? ''}`}>{ESTADO_RESERVA[r.estado]?.label ?? r.estado}</span></td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(r.total)}</td>
                </tr>
              ))}
            </tbody>
            {g.reservas.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold">
              <td colSpan={7} className="py-2">Total del grupo</td>
              <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(g.reservas.filter((r) => r.estado !== 'cancelada' && r.estado !== 'no_show').reduce((a, r) => a + Number(r.total), 0))}</td>
            </tr></tfoot>}
          </table>
        </CardContent>
      </Card>

      {editar && <ModalGrupo grupo={g} onClose={() => setEditar(false)} onDone={() => { setEditar(false); refrescar(); }} />}
    </div>
  );
}
